import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, ArrowRight } from "lucide-react";
import { BASE_URL, downloadApi } from "../../../../api/api";
import api from "../../../../utils/api";
import { Link } from "react-router-dom";

const SampleQuestion = () => {
    const [samplePdfs, setSamplePdfs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`${downloadApi}`)
            .then((res) => {
                setSamplePdfs(res.data || []);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => setLoading(false));
    }, []);

    const activePdfs = samplePdfs.filter(
        (pdf) => pdf.status === true || pdf.status === 1,
    );
    const displayPdfs = activePdfs.slice(0, 4);

    // Professional animation variants matching your theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    if (!loading && displayPdfs.length === 0) {
        return null;
    }

    return (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white w-full border-t border-gray-100">
            <div className="max-w-7xl mx-auto">
                {/* Standardized Editorial Header */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6 border-b border-gray-200 pb-6">
                    <div className="max-w-2xl">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3">
                                <span className="w-6 h-1 bg-red-700"></span>
                                Free Study Material
                            </div>
                            <h2 className="text-4xl md:text-5xl font-black text-neutral-900 mb-4">
                                Sample{" "}
                                <span className="text-yellow-500">
                                    Questions.
                                </span>
                            </h2>
                            <p className="text-gray-500 text-lg font-medium leading-relaxed">
                                Get a glimpse of real examination questions,
                                evaluated copies, and high-quality model
                                answers.
                            </p>
                        </motion.div>
                    </div>

                    {/* View More Button (Desktop) */}
                    {!loading && activePdfs.length > 4 && (
                        <Link
                            to="/downloads"
                            className="hidden md:inline-flex items-center px-6 py-2.5 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-lg shadow-sm transition-colors duration-300 group"
                        >
                            View All Resources
                            <ArrowRight
                                size={16}
                                className="ml-2 group-hover:translate-x-1 transition-transform"
                            />
                        </Link>
                    )}
                </div>

                {loading ? (
                    /* Loading Skeleton */
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm animate-pulse h-64"
                            ></div>
                        ))}
                    </div>
                ) : (
                    /* Professional Document Grid */
                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {displayPdfs.map((pdf, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                className="group relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 flex flex-col overflow-hidden"
                            >
                                {/* Subtle Hover Accent Line */}
                                <div className="absolute top-0 left-0 w-full h-[3px] bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500 ease-out z-10"></div>

                                <div className="p-6 flex flex-col flex-1">
                                    {/* Icon */}
                                    <div className="w-12 h-12 bg-gray-50 group-hover:bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center transition-colors duration-300 border border-gray-100 group-hover:border-yellow-400 mb-5 shadow-sm">
                                        <FileText size={22} />
                                    </div>

                                    {/* Content */}
                                    <h3 className="text-lg font-bold text-neutral-900 mb-2 group-hover:text-red-700 transition-colors duration-300 line-clamp-2">
                                        {pdf.name}
                                    </h3>

                                    <p className="text-gray-500 text-sm font-medium leading-relaxed line-clamp-3 mb-6 flex-1">
                                        {pdf.desc ||
                                            "Download this comprehensive PDF resource to boost your preparation strategy."}
                                    </p>

                                    {/* Download Action */}
                                    <div className="mt-auto pt-4 border-t border-gray-100">
                                        <a
                                            href={pdf.file_path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex cursor-pointer items-center justify-between w-full px-4 py-2.5 bg-yellow-400 hover:bg-neutral-900 hover:text-white text-neutral-900 font-bold rounded-lg text-sm transition-colors duration-300 group/btn"
                                        >
                                            <div className="flex items-center">
                                                <Download
                                                    size={16}
                                                    className="mr-2 group-hover/btn:text-white transition-colors"
                                                />
                                                <span>Download PDF</span>
                                            </div>
                                            <ArrowRight
                                                size={16}
                                                className="transform -translate-x-2 opacity-0 group-hover/btn:translate-x-0 group-hover/btn:opacity-100 transition-all duration-300"
                                            />
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}

                {/* Mobile View More Button (Hidden on Desktop) */}
                {!loading && activePdfs.length > 4 && (
                    <div className="mt-8 flex justify-center md:hidden">
                        <Link
                            to="/downloads"
                            className="inline-flex items-center w-full justify-center px-6 py-3 bg-neutral-900 text-white font-bold rounded-lg shadow-sm"
                        >
                            View All Resources
                            <ArrowRight size={16} className="ml-2" />
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
};

export default SampleQuestion;
